/**
 * quoteExtraFields — os campos EXTRAS nomeados pelo prestador (lacuna L5).
 *
 * ─── O PROBLEMA QUE ELE RESOLVE ─────────────────────────────────────────────
 *
 * O documento tem "Observações" e "Termos e condições", os dois fixos. Quem
 * precisa de "Nº da OS do cliente", "Centro de custo" ou "Placa do veículo"
 * enfia isso no meio das observações, onde o cliente não acha. Aqui o prestador
 * cria o RÓTULO, e o PDF imprime um par rótulo/valor.
 *
 * ─── AS REGRAS QUE QUEBRAM EM SILÊNCIO SE IGNORADAS ─────────────────────────
 *
 * **MEIO PAR NÃO É UM CAMPO.** Rótulo sem valor (ou valor sem rótulo) é
 * descartado na gravação: no papel isso viraria "Placa do veículo:" seguido de
 * nada, ou um valor solto sem dizer do que se trata. É a família do #61 —
 * ausência é ausência —, e o descarte tem de acontecer no BUILDER, porque o
 * formulário sempre tem uma linha em branco no fim enquanto o usuário digita.
 *
 * **A ORDEM É DADO.** É a ordem em que o prestador criou os campos, e é ela que
 * o PDF imprime. Um `Object` com chave→valor perderia isso (e impediria dois
 * campos com o mesmo rótulo, que é legítimo: "Medição 1", "Medição 2").
 *
 * **O TETO É VISÍVEL, NUNCA SILENCIOSO.** `MAX_EXTRA_FIELDS` existe porque o
 * PDF não tem layout automático (#13) e uma lista sem fim empurraria o rodapé
 * para fora da folha. A tela mostra o limite e desabilita o botão; o builder
 * corta como rede — e é por isso que ele NÃO pode ser a única barreira: corte
 * silencioso é o que o repo proíbe (#50).
 */

/**
 * Um par rótulo/valor, na ordem em que o prestador o criou.
 *
 * ⚠️ `type`, não `interface`, e isso NÃO é estilo: a coluna é `jsonb`, e o
 * tipo `Json` dos tipos gerados exige index signature. O TypeScript dá uma
 * implícita a type alias de objeto e **não** a interface — com `interface` o
 * payload não compila, com uma mensagem que fala de `Json[]` e não ajuda em
 * nada a achar a causa.
 */
export type QuoteExtraField = {
  label: string;
  value: string;
};

/**
 * Teto de campos por documento. Visível na tela (o botão desabilita e o texto
 * diz o limite) — este número é a rede, não a única barreira.
 */
export const MAX_EXTRA_FIELDS = 10;

/** Limites por campo, aplicados também como `maxLength` nos inputs da tela. */
export const MAX_EXTRA_LABEL = 40;
export const MAX_EXTRA_VALUE = 200;

const clean = (raw: unknown, max: number): string =>
  typeof raw === 'string' ? raw.trim().slice(0, max) : '';

/**
 * Leitor: da coluna `jsonb` (ou do cache) para a lista da aplicação.
 *
 * Tolerante de propósito — o `jsonb` pode ter sido gravado por uma versão
 * anterior do app ou editado por fora, e um `throw` aqui deixaria o documento
 * inteiro sem abrir. O que não casa com a forma é simplesmente ignorado.
 */
export function quoteExtraFieldsFromRow(row: Record<string, any> | null | undefined): QuoteExtraField[] {
  const raw = row?.extra_fields;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => ({
      label: clean(entry?.label, MAX_EXTRA_LABEL),
      value: clean(entry?.value, MAX_EXTRA_VALUE),
    }))
    .filter((field) => field.label !== '' && field.value !== '')
    .slice(0, MAX_EXTRA_FIELDS);
}

/**
 * Gravador: a coluna de uma vez.
 *
 * Espalhe (`...buildQuoteExtraFieldsColumns(data)`) em vez de escrever a chave
 * à mão — são ~13 payloads de `quotes`, montados por lista explícita, e o guard
 * de varredura cobra esta chamada dentro de cada literal (#33/#62). Extrair
 * para uma variável e espalhar a variável DESLIGA o guard (#75).
 *
 * Lista vazia grava `null`, não `[]`: os dois significam "sem campos extras", e
 * `null` é o que o histórico já tem — gravar `[]` faria toda linha divergir do
 * passado sem nenhum ganho.
 */
export function buildQuoteExtraFieldsColumns(
  info: { extraFields?: QuoteExtraField[] | null } | null | undefined,
): { extra_fields: QuoteExtraField[] | null } {
  const fields = (info?.extraFields ?? [])
    .map((field) => ({
      label: clean(field?.label, MAX_EXTRA_LABEL),
      value: clean(field?.value, MAX_EXTRA_VALUE),
    }))
    // Meio par não é um campo: no papel viraria um rótulo sem resposta.
    .filter((field) => field.label !== '' && field.value !== '')
    .slice(0, MAX_EXTRA_FIELDS);

  return { extra_fields: fields.length > 0 ? fields : null };
}
